// Goal card (shown once, when the tutorial ends or on the next join of a player who finished it earlier) and the
// "next goal" hint with its kiosk pointer. Client-only: the goal comes from common/src/goals.ts (rules in
// common/data/goals.json) and the character the client already has. Overlay only; the world never pauses.
import { WORLD } from '../../common/src/data';
import { TILE } from '../../common/src/map';
import type { Character } from '../../common/src/rules';
import type { Snapshot } from '../../common/src/protocol';
import { GOALS, pickGoal, type Goal } from '../../common/src/goals';
import { currentBeat, STORY } from '../../common/src/story';
import { Tutorial } from './tutorial';
import { storyOn } from './story-flags';
import { esc } from './menus/kit';

const CARD_KEY = 'nk.goalcard', BOSS_KEY = 'nk.bossdone';
const get = (k: string) => { try { return localStorage.getItem(k); } catch { return null; } };
const set = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* storage blocked: it may show again */ } };

export interface ActiveGoal extends Goal { target: { x: number; y: number } | null; label?: string; pointLabel?: string }

export class GoalUI {
  private el: HTMLDivElement;
  visible = false;
  private bossDone = get(BOSS_KEY) === '1';

  constructor() {
    this.el = document.createElement('div'); this.el.id = 'goalcard'; this.el.setAttribute('role', 'dialog'); this.el.setAttribute('aria-label', 'Your goal');
    this.el.innerHTML = `<h3>${esc(GOALS.goalCard.title)}</h3><ul>${GOALS.goalCard.lines.map(l => `<li>${esc(l)}</li>`).join('')}</ul>
      <div class="b"><button data-goal="ok" class="go">Got it <span class="dim">(Enter)</span></button></div>`;
    document.body.appendChild(this.el);
    this.el.addEventListener('click', e => { if ((e.target as HTMLElement).closest('button')) this.dismiss(); });
    addEventListener('keydown', e => {
      if (!this.visible || (e.code !== 'Enter' && e.code !== 'Escape')) return;
      e.preventDefault(); e.stopPropagation(); this.dismiss();
    }, true);
  }

  static seen() { return get(CARD_KEY) === '1'; }
  /** Show the card unless this browser has already seen it. */
  show() { if (GoalUI.seen() || this.visible) return; this.visible = true; this.el.style.display = 'block'; }
  dismiss() { if (!this.visible) return; this.visible = false; this.el.style.display = 'none'; set(CARD_KEY, '1'); }

  /** Call every frame. Returns what the HUD should show, or null (card up, tutorial running, menu open, job active). */
  update(o: { s: Snapshot; id: number; char: Character; x: number; y: number; hidden: boolean; inCar?: boolean; controls?: 'kb' | 'pad' | 'touch' }): ActiveGoal | null {
    const mine = o.s.c.find(c => c.parts.includes(o.id));
    if (mine?.k === 'boss' && !this.bossDone) { this.bossDone = true; set(BOSS_KEY, '1'); }
    if (o.hidden || this.visible) return null;
    if (storyOn()) return mine ? null : this.story({ ...o, inCar: o.inCar ?? false, controls: o.controls ?? 'kb' }); // story mode (?story=1) replaces the NEXT line for good: its beats, then the end line
    const g = pickGoal({ cred: o.char.cred, perkPoints: o.char.perkPoints, attrPoints: o.char.attrPoints, inJob: !!mine, bossDone: this.bossDone });
    if (!g) return null;
    let target: { x: number; y: number } | null = null;
    if (g.point === 'kiosk') {
      let bd = Infinity;
      for (const p of WORLD.pois) if (p.kind === 'kiosk') { const d = Math.hypot(p.x * TILE - o.x, p.y * TILE - o.y); if (d < bd) { bd = d; target = { x: p.x * TILE, y: p.y * TILE }; } }
    }
    return { ...g, target };
  }
  private origin: { x: number; y: number } | null = null;
  private moved = false;
  private marked = false;
  /** Story mode (?story=1): the current act-one beat, its pointer and its map pin. Ignores `hidden`, so the full map can pin it too. */
  story(o: { s: Snapshot; char: Character; x: number; y: number; inCar: boolean; controls: 'kb' | 'pad' | 'touch' }): (ActiveGoal & { pin: boolean }) | null {
    if (!storyOn()) return null;
    const beat = currentBeat(o.char);
    if (!beat) { // act one's beats are done: the story teaches what the tutorial would have, so this browser skips it later
      if (!this.marked) { this.marked = true; Tutorial.markSeen(); set(CARD_KEY, '1'); }
      return { id: 'story.end', text: STORY.endText, target: null, label: 'STORY', pin: false }; // and no NEXT line in story mode
    }
    this.origin ??= { x: o.x, y: o.y };
    if (!this.moved && Math.hypot(o.x - this.origin.x, o.y - this.origin.y) > 48) this.moved = true;
    const text = beat.id === STORY.act1[0].id && !this.moved ? `${STORY.moveHint[o.controls]} ${beat.text}` : beat.text;
    const t = beat.target.poi ? WORLD.pois.find(p => p.id === beat.target.poi) : null, car = o.s.sty; // sty: the marked car, until you are in it
    const target = car ?? (t ? { x: t.x * TILE, y: t.y * TILE } : null);
    return { id: beat.id, text, target, label: 'STORY', pointLabel: car ? 'CAR' : beat.point, pin: !!target };
  }
}